import { cn } from "@faire/prototype-playground/lib/utils";
import { TextInput } from "@faire/slate/components/text-input";
import { Core } from "@faire/slate/foundation/color";
import { Edit } from "@faire/slate-icons/Edit";
import { ImageLandscape } from "@faire/slate-icons/ImageLandscape";
import { Row } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import React from "react";

import { INLINE_SPE_COPY } from "./inlineSpeCopy";

/** SPE `ImageCell` uses a 60px square photo slot (column width 75px). */
export const VARIANT_PHOTO_SIZE_PX = 60;

export type MockVariantRow = {
  key: string;
  displayName: string;
  colorLabel: string;
  sizeLabel: string;
  /** Hex for mock color swatch (Flare-predicted). */
  swatchColor: string;
  /** Photo URL frozen when options were generated from an image snapshot. */
  photoUrl: string;
};

export const createEmptyVariantFieldMap = (
  rows: MockVariantRow[] = []
): Record<string, string> => Object.fromEntries(rows.map((r) => [r.key, ""]));

/** Grid column widths aligned with SPE `useColumns` (photo 75px, etc.). */
export const VARIANT_TABLE_GRID_CLASS =
  "grid-cols-[minmax(75px,75px)_minmax(64px,64px)_minmax(168px,1.25fr)_minmax(120px,140px)_minmax(148px,160px)_minmax(148px,160px)_minmax(88px,96px)_minmax(100px,112px)_minmax(100px,112px)]";

export const VARIANT_TABLE_MIN_WIDTH_CLASS = "min-w-[1104px]";

export const VARIANT_TABLE_CELL_CLASS =
  "flex min-h-[76px] min-w-0 items-center self-stretch";

export const computeMarginLabel = (
  wholesale: string,
  retail: string
): string => {
  const w = Number.parseFloat(wholesale.replace(/,/g, ""));
  const r = Number.parseFloat(retail.replace(/,/g, ""));
  if (!Number.isFinite(w) || !Number.isFinite(r) || r <= 0 || w < 0) {
    return INLINE_SPE_COPY.marginEmpty;
  }
  const marginPct = Math.round(((r - w) / r) * 100);
  return `${marginPct}%`;
};

export const MockUsdInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
}> = ({ value, onChange, placeholder, disabled }) => (
  <Row align="center" gap="2x" className="min-w-0 flex-1">
    <Typography variant="labelSansRegular" color={Core.text.subdued}>
      $
    </Typography>
    <div className="min-w-0 flex-1">
      <TextInput
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  </Row>
);

/** Empty / filled variant photo slot (SPE `ImageCell` + `ImageSelectionButton`). */
export const MockVariantPhotoCell: React.FC<{
  imageUrl: string | undefined;
  showFilledImage: boolean;
}> = ({ imageUrl, showFilledImage }) => {
  const hasImage = showFilledImage && Boolean(imageUrl);

  return (
    <button
      type="button"
      className={cn(
        "border-fs-border-muted bg-fs-surface-secondary group relative flex shrink-0 items-center justify-center overflow-hidden rounded-[var(--slate-radius-component-default)] border",
        "focus-visible:ring-fs-action-border-default focus-visible:ring-2 focus-visible:outline-none"
      )}
      style={{
        width: VARIANT_PHOTO_SIZE_PX,
        height: VARIANT_PHOTO_SIZE_PX,
      }}
      aria-label={INLINE_SPE_COPY.photoCellAriaLabel}
    >
      {hasImage ? (
        <img
          src={imageUrl}
          alt=""
          className="h-full w-full object-cover"
          width={VARIANT_PHOTO_SIZE_PX}
          height={VARIANT_PHOTO_SIZE_PX}
        />
      ) : (
        <ImageLandscape
          className="text-fs-icon-subdued"
          style={{
            width: 24,
            height: 24,
          }}
          aria-hidden
        />
      )}
      {!hasImage ? null : (
        <span className="bg-fs-surface-primary/80 absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
          <Edit
            className="text-fs-icon-primary"
            style={{ width: 20, height: 20 }}
            aria-hidden
          />
        </span>
      )}
    </button>
  );
};

export const MockVariantTableHeader: React.FC = () => (
  <div
    className={cn(
      "bg-fs-surface-secondary text-fs-text-primary border-fs-border-muted grid items-center gap-3 border-b px-3 py-3",
      VARIANT_TABLE_GRID_CLASS,
      VARIANT_TABLE_MIN_WIDTH_CLASS
    )}
    role="row"
  >
    <Typography variant="labelSansMedium">
      {INLINE_SPE_COPY.columnPhoto}
    </Typography>
    <Typography variant="labelSansMedium">
      {INLINE_SPE_COPY.columnSwatch}
    </Typography>
    <Typography variant="labelSansMedium">
      {INLINE_SPE_COPY.columnName}
    </Typography>
    <Typography variant="labelSansMedium">
      {INLINE_SPE_COPY.columnSku}
    </Typography>
    <Typography variant="labelSansMedium">
      {INLINE_SPE_COPY.columnWholesaleUs}
    </Typography>
    <Typography variant="labelSansMedium">
      {INLINE_SPE_COPY.columnRetailUs}
    </Typography>
    <Typography variant="labelSansMedium">
      {INLINE_SPE_COPY.columnMarginUs}
    </Typography>
    <Typography variant="labelSansMedium">
      {INLINE_SPE_COPY.columnOnHand}
    </Typography>
    <Typography variant="labelSansMedium">
      {INLINE_SPE_COPY.columnAvailable}
    </Typography>
  </div>
);

export const MockVariantTableRow: React.FC<{
  row: MockVariantRow;
  showVariantPhotos: boolean;
  sku: string;
  wholesaleDisplay: string;
  retailDisplay: string;
  onHand: string;
  onSku: (v: string) => void;
  onWholesale: (v: string) => void;
  onRetail: (v: string) => void;
  onOnHand: (v: string) => void;
}> = ({
  row,
  showVariantPhotos,
  sku,
  wholesaleDisplay,
  retailDisplay,
  onHand,
  onSku,
  onWholesale,
  onRetail,
  onOnHand,
}) => {
  const onHandTrim = onHand.trim();
  const availableDisplay = onHandTrim === "" ? "" : onHandTrim;

  return (
    <div
      className={cn(
        "border-fs-border-muted grid items-center gap-3 border-b px-3 py-2 last:border-b-0",
        VARIANT_TABLE_GRID_CLASS,
        VARIANT_TABLE_MIN_WIDTH_CLASS
      )}
      role="row"
    >
      <div className={cn(VARIANT_TABLE_CELL_CLASS, "justify-center")}>
        <MockVariantPhotoCell
          imageUrl={row.photoUrl}
          showFilledImage={showVariantPhotos}
        />
      </div>
      <div className={cn(VARIANT_TABLE_CELL_CLASS, "justify-center")}>
        <div
          className="border-fs-border-muted h-8 w-8 shrink-0 rounded-full border"
          style={{ backgroundColor: row.swatchColor }}
          title={row.colorLabel}
          aria-hidden
        />
      </div>
      <div className={VARIANT_TABLE_CELL_CLASS}>
        <Typography variant="paragraphSansRegular" color={Core.text.primary}>
          {row.displayName}
        </Typography>
      </div>
      <div className={VARIANT_TABLE_CELL_CLASS}>
        <TextInput
          value={sku}
          onChange={(e) => {
            onSku(e.target.value);
          }}
          placeholder={INLINE_SPE_COPY.skuPlaceholder}
        />
      </div>
      <div className={VARIANT_TABLE_CELL_CLASS}>
        <MockUsdInput
          value={wholesaleDisplay}
          onChange={onWholesale}
          placeholder="0.00"
        />
      </div>
      <div className={VARIANT_TABLE_CELL_CLASS}>
        <MockUsdInput
          value={retailDisplay}
          onChange={onRetail}
          placeholder="0.00"
        />
      </div>
      <div className={VARIANT_TABLE_CELL_CLASS}>
        <Typography variant="labelSansRegular" color={Core.text.subdued}>
          {computeMarginLabel(wholesaleDisplay, retailDisplay)}
        </Typography>
      </div>
      <div className={VARIANT_TABLE_CELL_CLASS}>
        <TextInput
          value={onHand}
          onChange={(e) => {
            onOnHand(e.target.value);
          }}
          placeholder="0"
        />
      </div>
      <div className={VARIANT_TABLE_CELL_CLASS}>
        <TextInput
          value={availableDisplay}
          readOnly
          disabled
          placeholder="0"
          aria-label={INLINE_SPE_COPY.availableAriaLabel}
        />
      </div>
    </div>
  );
};
